import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiPatch, apiDelete, formatDateTime, formatLKR, formatPct } from "@/lib/api";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import { CompanyLink, SymbolLink } from "@/components/common/StockLinks";
import HelpTooltip from "@/components/common/HelpTooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Trash2, RefreshCcw, Bell, BellRing, AlertTriangle, History, Sparkles, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const stripBullet = (s) => (s || "").replace(/^[\s✅⚠•·\-–—]+/, "").trim();

const ALERT_TYPE_META = {
  price_below: {
    label: "Price below",
    description: "Notify when last close drops to or below the target",
    needsTarget: true,
    targetLabel: "Target price (LKR)",
    formatTarget: (v) => formatLKR(v),
    icon: "⤓",
  },
  price_above: {
    label: "Price above",
    description: "Notify when last close rises to or above the target",
    needsTarget: true,
    targetLabel: "Target price (LKR)",
    formatTarget: (v) => formatLKR(v),
    icon: "⤒",
  },
  signal_buy: {
    label: "Signal becomes BUY",
    description: "Notify when AI signal changes to BUY",
    needsTarget: false,
    icon: "🟢",
  },
  score_drop: {
    label: "Score drop",
    description: "Notify when AI score drops by N points or more",
    needsTarget: true,
    targetLabel: "Drop threshold (points)",
    targetDefault: 10,
    formatTarget: (v) => `${v} pts`,
    icon: "📉",
  },
  dividend_coming: {
    label: "Dividend coming (7 days)",
    description: "Notify 7 days before ex-dividend date — only for stocks held in a portfolio",
    needsTarget: false,
    icon: "💰",
  },
  yield_target: {
    label: "Yield target",
    description: "Notify when current dividend yield rises to or above the target %",
    needsTarget: true,
    targetLabel: "Yield target (%)",
    formatTarget: (v) => formatPct(v),
    icon: "📈",
  },
  // Phase 7 Stage 5 — enhanced alert types
  signal_change: {
    label: "Signal change",
    description: "Notify when the composite signal changes (any change, upgrade to BUY, etc.)",
    needsTarget: false,
    icon: "🚦",
  },
  graham_discount: {
    label: "Graham discount",
    description: "Notify when the share price drops below its Graham Number (intrinsic value floor)",
    needsTarget: false,
    icon: "🛡️",
  },
  new_report: {
    label: "New CSE report",
    description: "Notify when a new annual or interim report is downloaded from the CSE",
    needsTarget: false,
    icon: "📄",
  },
  score_threshold: {
    label: "Score threshold",
    description: "Notify when composite score crosses your target level (upward only)",
    needsTarget: true,
    targetLabel: "Target score (0-100)",
    targetDefault: 65,
    formatTarget: (v) => (v == null ? "—" : `${v} pts`),
    icon: "🎯",
  },
};

const TYPE_OPTIONS = Object.entries(ALERT_TYPE_META).map(([k, m]) => ({ value: k, label: m.label }));

const AlertsPage = () => {
  const [securities, setSecurities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([apiGet("/securities"), apiGet("/alerts")])
      .then(([sec, al]) => {
        setSecurities(sec);
        setAlerts(al);
      })
      .catch((e) => setError(e.message || "Failed to load alerts"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const triggerCheckNow = async () => {
    setChecking(true);
    try {
      const res = await apiPost("/alerts/check-now");
      toast.success(`Checked ${res.checked} alert${res.checked === 1 ? "" : "s"} — ${res.fired} fired`);
      window.dispatchEvent(new Event("cse:alerts-updated"));
      load();
    } catch (e) {
      toast.error("Alert check failed");
    } finally {
      setChecking(false);
    }
  };

  const onCreated = (created) => {
    setAlerts((prev) => [created, ...prev]);
    setCreateOpen(false);
    toast.success("Alert created");
    window.dispatchEvent(new Event("cse:alerts-updated"));
    // Run a single check so newly-eligible alerts fire immediately
    triggerCheckNow();
  };

  const onUpdate = async (alert_id, patch) => {
    try {
      const updated = await apiPatch(`/alerts/${alert_id}`, patch);
      setAlerts((prev) => prev.map((a) => (a.alert_id === alert_id ? updated : a)));
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const onReset = async (alert_id) => {
    try {
      const updated = await apiPost(`/alerts/${alert_id}/reset`);
      setAlerts((prev) => prev.map((a) => (a.alert_id === alert_id ? updated : a)));
      toast.success("Alert reactivated");
      window.dispatchEvent(new Event("cse:alerts-updated"));
    } catch {
      toast.error("Reset failed");
    }
  };

  const onDelete = async (alert_id) => {
    if (!window.confirm("Delete this alert? This cannot be undone.")) return;
    try {
      await apiDelete(`/alerts/${alert_id}`);
      setAlerts((prev) => prev.filter((a) => a.alert_id !== alert_id));
      toast.success("Alert deleted");
      window.dispatchEvent(new Event("cse:alerts-updated"));
    } catch {
      toast.error("Delete failed");
    }
  };

  const active = useMemo(() => alerts.filter((a) => !a.is_triggered), [alerts]);
  const triggered = useMemo(() => alerts.filter((a) => a.is_triggered), [alerts]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState description={error} />;

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold inline-flex items-center">
            Custom alerts
            <HelpTooltip termId="buy-signal" size="xs" />
          </h2>
          <p className="text-xs text-muted-foreground">
            Watch for price thresholds, AI signal changes, score drops, dividend dates and yield targets.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={triggerCheckNow}
            disabled={checking}
            className="gap-2"
            data-testid="alerts-check-now-button"
          >
            <RefreshCcw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
            Check now
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="alerts-create-button">
                <Plus className="h-4 w-4" /> Create alert
              </Button>
            </DialogTrigger>
            <CreateAlertDialog
              securities={securities}
              onCreated={onCreated}
              onCancel={() => setCreateOpen(false)}
            />
          </Dialog>
        </div>
      </div>

      {/* Active alerts */}
      <Card className="border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display flex items-center gap-2 text-base">
            <Bell className="h-4 w-4 text-primary" />
            Active alerts <span className="text-muted-foreground text-sm">({active.length})</span>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            These will fire when their condition is met on the next scheduled run.
          </p>
        </CardHeader>
        <CardContent>
          {active.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="No active alerts yet"
              hint="Click 'Create alert' to be notified about price moves, signal changes or upcoming dividends."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {active.map((a) => (
                <ActiveAlertCard
                  key={a.alert_id}
                  alert={a}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Triggered alerts */}
      <Card className="border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display flex items-center gap-2 text-base">
            <History className="h-4 w-4 text-[hsl(var(--warning))]" />
            Triggered alerts <span className="text-muted-foreground text-sm">({triggered.length})</span>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            History of fired alerts. Reset an alert to reactivate it; delete to remove permanently.
          </p>
        </CardHeader>
        <CardContent>
          {triggered.length === 0 ? (
            <EmptyState
              icon={BellRing}
              title="Nothing has triggered yet"
              hint="Triggered alerts will appear here so you have a record of every signal that fired."
            />
          ) : (
            <ul className="divide-y divide-border rounded-lg border bg-background overflow-hidden">
              {triggered.map((a) => (
                <TriggeredRow
                  key={a.alert_id}
                  alert={a}
                  onReset={onReset}
                  onDelete={onDelete}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ===========================================================================
// Subcomponents
// ===========================================================================

const EmptyState = ({ icon: Icon, title, hint }) => (
  <div className="py-10 text-center text-sm text-muted-foreground">
    <Icon className="h-6 w-6 mx-auto mb-2 opacity-60" />
    <div className="font-medium text-foreground">{title}</div>
    <p className="mt-1 text-xs">{hint}</p>
  </div>
);

const computeProgress = (alert) => {
  const cur = Number(alert.current_value);
  const tgt = Number(alert.target_value);
  if (!Number.isFinite(cur) || !Number.isFinite(tgt) || tgt === 0) return null;
  switch (alert.alert_type) {
    case "price_below":
      // distance: 0% means already at/under target. Up to 50% above target -> 0%.
      if (cur <= tgt) return 100;
      return Math.max(0, Math.min(100, 100 - ((cur - tgt) / tgt) * 100));
    case "price_above":
      if (cur >= tgt) return 100;
      return Math.max(0, Math.min(100, (cur / tgt) * 100));
    case "yield_target":
      if (cur >= tgt) return 100;
      return Math.max(0, Math.min(100, (cur / tgt) * 100));
    case "score_drop":
      // current_value here is current score; baseline comparison handled server-side
      return null;
    case "score_threshold":
      // current_value is the latest score; tgt is the trigger level
      if (cur >= tgt) return 100;
      return Math.max(0, Math.min(100, (cur / tgt) * 100));
    default:
      return null;
  }
};

const ActiveAlertCard = ({ alert, onUpdate, onDelete }) => {
  const meta = ALERT_TYPE_META[alert.alert_type] || {};
  const progress = computeProgress(alert);
  const targetText = meta.formatTarget ? meta.formatTarget(alert.target_value) : "—";
  const currentText = meta.formatTarget && alert.current_value != null
    ? meta.formatTarget(alert.current_value)
    : alert.current_value != null
      ? String(alert.current_value)
      : "—";

  return (
    <div
      className="rounded-lg border bg-background p-4 space-y-3"
      data-testid={`alert-card-${alert.alert_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="text-[10px] uppercase">
              <span className="mr-1">{meta.icon}</span>
              {meta.label}
            </Badge>
            {!alert.is_active && (
              <Badge variant="outline" className="text-[10px]">disabled</Badge>
            )}
          </div>
          <div className="mt-1">
            <CompanyLink
              companyCode={alert.company_code}
              name={alert.company_name}
              fromLabel="Alerts"
              className="text-sm font-medium block"
            />
            <SymbolLink
              companyCode={alert.company_code}
              symbol={alert.cse_symbol}
              fromLabel="Alerts"
              className="text-muted-foreground"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={!!alert.is_active}
            onCheckedChange={(v) => onUpdate(alert.alert_id, { is_active: v })}
            data-testid={`alert-toggle-${alert.alert_id}`}
            aria-label="Enable or disable alert"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(alert.alert_id)}
            data-testid={`alert-delete-${alert.alert_id}`}
            className="text-muted-foreground hover:text-[hsl(var(--destructive))]"
            aria-label="Delete alert"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="text-sm font-medium">{alert.alert_name}</div>
      <p className="text-xs text-muted-foreground">{meta.description}</p>

      {meta.needsTarget && (
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="rounded-md border bg-card px-3 py-2">
            <div className="text-muted-foreground">Target</div>
            <div className="font-semibold tabular text-foreground">{targetText}</div>
          </div>
          <div className="rounded-md border bg-card px-3 py-2">
            <div className="text-muted-foreground">Current</div>
            <div className="font-semibold tabular text-foreground">{currentText}</div>
          </div>
        </div>
      )}

      {progress != null && (
        <div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
            <span>Progress to trigger</span>
            <span className="tabular">{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-[hsl(var(--primary))] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>Created {formatDateTime(alert.created_at)}</span>
        {alert.last_checked_at && <span>Checked {formatDateTime(alert.last_checked_at)}</span>}
      </div>
    </div>
  );
};

const TriggeredRow = ({ alert, onReset, onDelete }) => {
  const meta = ALERT_TYPE_META[alert.alert_type] || {};
  return (
    <li
      className="px-4 py-3 flex flex-wrap items-start justify-between gap-3 hover:bg-muted/40"
      data-testid={`triggered-row-${alert.alert_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            variant="outline"
            className="text-[10px] gap-1 bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"
          >
            <AlertTriangle className="h-3 w-3" /> {meta.label || alert.alert_type}
          </Badge>
          <SymbolLink
            companyCode={alert.company_code}
            symbol={alert.cse_symbol}
            fromLabel="Alerts"
          />
          {alert.company_name && (
            <span className="text-xs text-muted-foreground">{alert.company_name}</span>
          )}
        </div>
        <p className="mt-1 text-sm">{alert.trigger_message || alert.alert_name}</p>
        {(alert.rationale_pro || alert.rationale_con) && (
          <div
            className="mt-2 space-y-1"
            data-testid={`triggered-rationale-${alert.alert_id}`}
          >
            {alert.rationale_pro && (
              <div className="flex gap-1.5 text-xs leading-snug">
                <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0 text-[hsl(var(--success))]" />
                <span className="text-foreground/85">
                  {stripBullet(alert.rationale_pro)}
                </span>
              </div>
            )}
            {alert.rationale_con && (
              <div className="flex gap-1.5 text-xs leading-snug">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-[hsl(var(--warning))]" />
                <span className="text-foreground/85">
                  {stripBullet(alert.rationale_con)}
                </span>
              </div>
            )}
          </div>
        )}
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Triggered {formatDateTime(alert.triggered_at)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onReset(alert.alert_id)}
          data-testid={`triggered-reset-${alert.alert_id}`}
          className="gap-1.5"
        >
          <RefreshCcw className="h-3.5 w-3.5" /> Reset
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(alert.alert_id)}
          data-testid={`triggered-delete-${alert.alert_id}`}
          className="text-muted-foreground hover:text-[hsl(var(--destructive))]"
          aria-label="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </li>
  );
};

const SIGNAL_CHANGE_OPTIONS = [
  { value: "any_change", label: "Any change" },
  { value: "to_buy", label: "Upgrade to BUY" },
  { value: "to_watch", label: "Upgrade to WATCH" },
  { value: "any_deterioration", label: "Any deterioration" },
];

const REPORT_TYPE_CHOICES = [
  { value: "annual", label: "Annual reports" },
  { value: "interim", label: "Interim/Quarterly reports" },
  { value: "other", label: "Other filings" },
];

const CreateAlertDialog = ({ securities, onCreated, onCancel }) => {
  const [cseSymbol, setCseSymbol] = useState("");
  const [alertType, setAlertType] = useState("price_below");
  const [targetValue, setTargetValue] = useState("");
  const [alertName, setAlertName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Phase 7 Stage 5 — per-type extras
  const [watchFor, setWatchFor] = useState("any_change");
  const [reportTypes, setReportTypes] = useState(["annual", "interim"]);
  const [stockContext, setStockContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  const meta = ALERT_TYPE_META[alertType] || {};

  // Fetch context (price + composite score) when relevant alert types are chosen
  React.useEffect(() => {
    setStockContext(null);
    if (!cseSymbol) return;
    if (!["graham_discount", "score_threshold", "signal_change"].includes(alertType)) return;
    let cancelled = false;
    setContextLoading(true);
    apiGet(`/compare?symbols=${cseSymbol}`)
      .then((res) => {
        if (cancelled) return;
        const r = res?.results?.[0];
        if (!r || !r.found) {
          setStockContext(null);
          return;
        }
        const close = r.price?.close;
        const graham = r.score?.layer4_graham_number;
        const discountPct =
          close != null && graham != null && graham > 0
            ? Math.round((1 - close / graham) * 1000) / 10
            : null;
        setStockContext({
          final_score: r.score?.final_score,
          signal: r.score?.signal,
          layer4_graham_number: graham,
          close,
          graham_discount_pct: discountPct, // positive = below Graham
        });
      })
      .catch(() => setStockContext(null))
      .finally(() => !cancelled && setContextLoading(false));
    return () => { cancelled = true; };
  }, [cseSymbol, alertType]);

  const toggleReportType = (val) => {
    setReportTypes((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const submit = async () => {
    if (!cseSymbol) {
      toast.error("Choose a stock");
      return;
    }
    if (meta.needsTarget && (targetValue === "" || isNaN(Number(targetValue)))) {
      toast.error("Enter a valid target value");
      return;
    }
    if (alertType === "new_report" && reportTypes.length === 0) {
      toast.error("Select at least one report type to watch");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        cse_symbol: cseSymbol,
        alert_type: alertType,
        target_value: meta.needsTarget ? Number(targetValue) : null,
        alert_name: alertName || null,
      };
      if (alertType === "signal_change") payload.watch_for = watchFor;
      if (alertType === "new_report") payload.report_types = reportTypes;
      const created = await apiPost("/alerts", payload);
      onCreated(created);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create alert";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent
      className="sm:max-w-[480px]"
      data-testid="alerts-create-dialog"
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>Create new alert</DialogTitle>
        <DialogDescription>{meta.description}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="space-y-1.5">
          <Label htmlFor="alert-symbol">Stock</Label>
          <Select value={cseSymbol} onValueChange={setCseSymbol}>
            <SelectTrigger id="alert-symbol" data-testid="alert-symbol-select">
              <SelectValue placeholder="Choose a security" />
            </SelectTrigger>
            <SelectContent>
              {securities.map((s) => (
                <SelectItem key={s.cse_symbol} value={s.cse_symbol}>
                  {s.cse_symbol} — {s.company_name}{" "}
                  <span className="text-muted-foreground ml-1 text-xs capitalize">
                    {(s.share_type || "").replace("_", "-")}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="alert-type">Alert type</Label>
          <Select
            value={alertType}
            onValueChange={(v) => {
              setAlertType(v);
              const m = ALERT_TYPE_META[v];
              if (m?.targetDefault != null && !targetValue) {
                setTargetValue(String(m.targetDefault));
              }
            }}
          >
            <SelectTrigger id="alert-type" data-testid="alert-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {ALERT_TYPE_META[opt.value].icon} {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {meta.needsTarget && (
          <div className="space-y-1.5">
            <Label htmlFor="alert-target">{meta.targetLabel}</Label>
            <Input
              id="alert-target"
              type="number"
              step="0.01"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              placeholder={meta.targetDefault != null ? String(meta.targetDefault) : ""}
              data-testid="alert-target-input"
            />
          </div>
        )}

        {/* Phase 7 Stage 5 — Signal change watch_for selector */}
        {alertType === "signal_change" && (
          <div className="space-y-1.5">
            <Label htmlFor="alert-watch-for">Notify me when signal changes to…</Label>
            <Select value={watchFor} onValueChange={setWatchFor}>
              <SelectTrigger id="alert-watch-for" data-testid="alert-watchfor-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SIGNAL_CHANGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Phase 7 Stage 5 — New-report report types */}
        {alertType === "new_report" && (
          <div className="space-y-1.5">
            <Label>Report types to watch</Label>
            <div className="space-y-1.5">
              {REPORT_TYPE_CHOICES.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={reportTypes.includes(opt.value)}
                    onChange={() => toggleReportType(opt.value)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                    data-testid={`alert-reporttype-${opt.value}`}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Phase 7 Stage 5 — Stock context for graham_discount / score_threshold / signal_change */}
        {cseSymbol && ["graham_discount", "score_threshold", "signal_change"].includes(alertType) && (
          <div
            className="rounded-md border bg-muted/30 px-3 py-2 text-xs space-y-0.5"
            data-testid="alert-context-panel"
          >
            <div className="text-muted-foreground uppercase tracking-wide text-[10px]">
              {cseSymbol} current state
            </div>
            {contextLoading && <div>Loading…</div>}
            {!contextLoading && stockContext && alertType === "graham_discount" && (
              <div className="text-foreground/90 leading-snug space-y-0.5">
                <div>
                  Graham Number:{" "}
                  <span className="font-mono">
                    Rs. {stockContext.layer4_graham_number != null ? Number(stockContext.layer4_graham_number).toFixed(2) : "—"}
                  </span>
                </div>
                <div>
                  Current Price:{" "}
                  <span className="font-mono">
                    Rs. {stockContext.close != null ? Number(stockContext.close).toFixed(2) : "—"}
                  </span>
                </div>
                {stockContext.graham_discount_pct != null && (
                  <div className="text-[hsl(var(--success))]">
                    {stockContext.graham_discount_pct > 0
                      ? `(trading ${stockContext.graham_discount_pct.toFixed(0)}% below Graham already)`
                      : `(trading ${Math.abs(stockContext.graham_discount_pct).toFixed(0)}% above Graham)`}
                  </div>
                )}
              </div>
            )}
            {!contextLoading && stockContext && alertType === "score_threshold" && (
              <div className="text-foreground/90 leading-snug">
                Current score:{" "}
                <span className="font-mono font-semibold">{stockContext.final_score ?? "—"}</span>
                {" · "}
                signal:{" "}
                <span className="font-mono">{stockContext.signal || "—"}</span>
              </div>
            )}
            {!contextLoading && stockContext && alertType === "signal_change" && (
              <div className="text-foreground/90 leading-snug">
                Current signal:{" "}
                <span className="font-mono font-semibold">{stockContext.signal || "—"}</span>
                {" · "}
                score: <span className="font-mono">{stockContext.final_score ?? "—"}</span>
              </div>
            )}
            {!contextLoading && !stockContext && (
              <div className="text-muted-foreground">No score data available yet for {cseSymbol}.</div>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="alert-name">Name (optional)</Label>
          <Input
            id="alert-name"
            value={alertName}
            onChange={(e) => setAlertName(e.target.value)}
            placeholder="Auto-generated if blank"
            data-testid="alert-name-input"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={submitting} data-testid="alert-submit-button">
          {submitting ? "Creating…" : "Create alert"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export default AlertsPage;
